import React, { useState } from "react";
import { Play } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { waUrl } from "../../config/bdk";

const VIDEOS = [
  {
    id: "fQmHTnMCxTI",
    title: "Promoción para nuevos clientes",
    description:
      "Ideal para anunciar inscripciones, mensualidades, promociones de temporada o servicios muestra.",
  },
  {
    id: "od-xDfv5RyU",
    title: "Imagen de marca del negocio",
    description:
      "Ideal para mostrar valores, motivación, instalaciones, comunidad y estilo del negocio.",
  },
];

// Facade pattern: only load the YouTube iframe after the user clicks play.
// This keeps the page fast and avoids loading YouTube's SDK just to show a thumbnail.
const VideoCard = ({ v, delay }) => {
  const [loaded, setLoaded] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`;

  return (
    <article
      data-testid={`video-card-${v.id}`}
      className="rounded-[14px] overflow-hidden bdk-reveal bdk-reveal--up"
      style={{
        background: "var(--ink-2)",
        border: "1px solid #2a2b34",
        "--reveal-delay": `${delay}ms`,
      }}
    >
      {/* Vertical 9:16 video area */}
      <div
        className="relative w-full mx-auto"
        style={{
          aspectRatio: "9 / 16",
          maxHeight: "620px",
          background: "#000",
        }}
      >
        {loaded ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            aria-label={`Reproducir ${v.title}`}
            data-testid={`video-play-${v.id}`}
            className="group absolute inset-0 w-full h-full cursor-pointer"
          >
            <img
              src={thumb}
              alt={v.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity"
            />
            <span
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 50% 45%, rgba(217,0,32,.35), transparent 60%), linear-gradient(180deg, rgba(6,6,8,.35) 0%, rgba(6,6,8,.75) 100%)",
              }}
            />
            <span className="absolute inset-0 grid place-items-center">
              <span className="flex flex-col items-center gap-4 text-center px-6">
                <span
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full grid place-items-center transition-transform group-hover:scale-110"
                  style={{
                    background: "var(--red)",
                    boxShadow: "0 15px 40px rgba(217,0,32,.5)",
                  }}
                >
                  <Play
                    size={26}
                    fill="#fff"
                    className="text-white ml-0.5"
                  />
                </span>
                <span>
                  <span
                    className="block font-body font-black uppercase text-[10px] md:text-[11px]"
                    style={{
                      letterSpacing: "3px",
                      color: "#ff5f6f",
                    }}
                  >
                    Reel · YouTube Shorts
                  </span>
                  <span className="block font-display text-[18px] md:text-[20px] text-white mt-1">
                    Reproducir video
                  </span>
                </span>
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="p-6 md:p-7">
        <h3 className="font-display text-[22px] md:text-[24px] uppercase leading-tight">
          {v.title}
        </h3>
        <p className="mt-2 text-[#b8b9c1] text-[13px] md:text-[14px]">
          {v.description}
        </p>
      </div>
    </article>
  );
};

const VideoExamples = () => {
  useReveal();

  return (
    <section
      id="ejemplos-videos"
      data-testid="section-ejemplos-videos"
      className="bdk-section relative overflow-hidden isolate"
      style={{ background: "var(--ink)", color: "#fff" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, rgba(217,0,32,.18), transparent 45%)",
        }}
      />

      <div className="container-bdk relative">
        <div className="max-w-[760px] mx-auto text-center mb-14 bdk-reveal bdk-reveal--up">
          <p className="kicker">Contenido dinámico</p>
          <h2 className="font-display text-[38px] sm:text-[48px] md:text-[56px] lg:text-[64px]">
            Videos promocionales
            <br />
            con IA para tu negocio.
          </h2>
          <p className="mt-5 text-[#b9bac3] text-[15px] md:text-[16px]">
            Creamos videos cortos verticales para historias, reels, estados de
            WhatsApp o anuncios digitales.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-[820px] mx-auto">
          {VIDEOS.map((v, i) => (
            <VideoCard key={v.id} v={v} delay={i * 100} />
          ))}
        </div>

        {/* Inline CTA */}
        <div
          className="mt-12 rounded-[14px] p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 bdk-reveal bdk-reveal--up"
          style={{
            background: "var(--ink-2)",
            border: "1px solid #2a2b34",
            borderLeft: "4px solid var(--red)",
          }}
        >
          <div>
            <h3 className="font-display text-[24px] md:text-[26px] uppercase leading-tight text-white">
              ¿Quieres un video promocional para tu negocio?
            </h3>
            <p className="text-[#b8b9c1] text-[13px] mt-2">
              Creamos videos verticales pensados para redes sociales y anuncios
              digitales.
            </p>
          </div>
          <a
            href={waUrl(
              "Hola, vi la sección de videos promocionales y quiero información para crear un video para mi negocio."
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="videos-inline-cta"
            className="bdk-btn bdk-btn--primary flex-shrink-0"
          >
            Quiero un video para mi negocio
          </a>
        </div>
      </div>
    </section>
  );
};

export default VideoExamples;
